import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useCsrfToken } from '../../Hooks/useCsrfToken';
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';

function RosterScheduleCard({
	rosterId,
	getRosterScheduleDataEndpoint,
	scheduleUpdated,
	isScraping
}) {
	const { user } = useAuth();
	const csrfToken = useCsrfToken();
	const [
		schedule,
		setSchedule
	] = useState([]);
	const [
		loading,
		setLoading
	] = useState(true);
	const [
		error,
		setError
	] = useState(null);
	const [
		leagueRosters,
		setLeagueRosters
	] = useState([]);
	const [
		rosterInfo,
		setRosterInfo
	] = useState(null);

	useEffect(
		() => {
			if (csrfToken) {
				fetchSchedule();
			}
		},
		[
			scheduleUpdated,
			rosterId,
			csrfToken
		]
	);

	const fetchSchedule = async () => {
		try {
			setLoading(true);
			setError(null);

			// Use lightweight endpoint to get only schedule and minimal league roster data
			const scheduleDataUrl = getRosterScheduleDataEndpoint
				.replace('user_id', user.id)
				.replace('roster_id', rosterId);

			const response = await fetch(scheduleDataUrl, {
				credentials: 'same-origin',
				cache: 'no-cache',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRF-Token': csrfToken,
					'Cache-Control': 'no-cache',
					Pragma: 'no-cache'
				}
			});

			const data = await response.json();

			if (data.success && data.data) {
				// Set minimal roster info for UI checks
				setRosterInfo({
					roster_url: data.data.roster_url,
					league: data.data.league
				});

				// Set schedule data from roster_schedules
				if (data.data.roster_schedules) {
					setSchedule(data.data.roster_schedules);
				} else {
					setSchedule([]);
				}

				// Set league rosters for avatar lookup
				if (data.data.league_rosters) {
					setLeagueRosters(data.data.league_rosters);
				}
			} else {
				setError('Failed to load schedule data');
			}
		} catch (err) {
			console.error('Error fetching schedule:', err);
			setError('Failed to load schedule');
		} finally {
			setLoading(false);
		}
	};

	const getResultBadge = (result) => {
		if (!result) return null;

		const colorClasses = {
			W: 'bg-green-100 text-green-800',
			L: 'bg-red-100 text-red-800',
			T: 'bg-gray-100 text-gray-800'
		};

		return (
			<span
				className={`px-2 py-1 rounded text-sm font-semibold ${colorClasses[
					result
				]}`}
			>
				{result}
			</span>
		);
	};

	const getOpponentAvatar = (opponentName) => {
		const opponent = leagueRosters.find(
			(roster) => roster.team_name === opponentName
		);
		return opponent ? opponent.image_url : null;
	};

	return (
		<div className="mt-14">
			<div className="text-3xl font-bold mb-3 text-gray-800 dark:text-dark-text">
				<h4>Schedule & Matchups</h4>
			</div>
			<div className="border border-gray-300 dark:border-dark-border rounded-xl shadow-lg bg-white dark:bg-dark-card p-6">
				{loading || isScraping ? (
					<div className="text-center">
						<LoadingSpinner />
					</div>
				) : (
					<React.Fragment>
						{error && (
							<div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
								<p>{error}</p>
							</div>
						)}

						{!rosterInfo ||
						!rosterInfo.league ||
						(rosterInfo.league.platform !== 'FFPC' &&
							rosterInfo.league.platform !== 'Sleeper' &&
							rosterInfo.league.platform !== 'Yahoo') ||
						!rosterInfo.roster_url ? (
							<div className="text-gray-500 text-center py-12">
								<i className="fas fa-calendar-alt text-6xl mb-4 block opacity-30" />
								<p className="text-lg font-medium mb-2">
									Schedule Not Available
								</p>
								<p className="text-sm">
									Schedule tracking is currently only available for FFPC,
									Sleeper, and Yahoo rosters with a roster URL.
								</p>
							</div>
						) : schedule.length === 0 ? (
							<div className="text-gray-500 text-center py-12">
								<i className="fas fa-calendar-alt text-6xl mb-4 block opacity-30" />
								<p className="text-lg font-medium mb-2">No Schedule Data</p>
								<p className="text-sm">
									Schedule will load automatically when available.
								</p>
							</div>
						) : (
							<div className="overflow-x-auto -mx-6 px-6 sm:mx-0 sm:px-0">
								<table className="min-w-full divide-y divide-gray-200">
									<thead className="bg-gray-50 dark:bg-gray-800">
										<tr>
											<th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
												Week
											</th>
											<th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
												Opponent
											</th>
											<th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
												Result
											</th>
											<th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
												Score
											</th>
										</tr>
									</thead>
									<tbody className="bg-white dark:bg-dark-card divide-y divide-gray-200 dark:divide-gray-700">
										{schedule.map((game, index) => {
											const avatarUrl = getOpponentAvatar(game.opponent);
											return (
												<tr
													key={index}
													className="hover:bg-gray-50 dark:hover:bg-gray-800"
												>
													<td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
														Week {game.week}
													</td>
													<td className="px-3 sm:px-6 py-4 text-sm text-gray-700 dark:text-gray-300">
														<div className="flex items-center gap-2 sm:gap-3">
															{avatarUrl && (
																<img
																	src={avatarUrl}
																	alt={`${game.opponent} logo`}
																	className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover border border-gray-300 dark:border-gray-700 shadow-sm flex-shrink-0"
																	onError={(e) => {
																		e.target.style.display = 'none';
																	}}
																/>
															)}
															<span className="truncate">{game.opponent}</span>
														</div>
													</td>
													<td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm">
														{game.result ? (
															getResultBadge(game.result)
														) : (
															<span className="text-gray-400 dark:text-gray-500 italic">
																TBD
															</span>
														)}
													</td>
													<td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
														{game.final_score ? (
															game.final_score
														) : (
															<span className="text-gray-400 dark:text-gray-500 italic">
																-
															</span>
														)}
													</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</div>
						)}
					</React.Fragment>
				)}
			</div>
		</div>
	);
}

export default RosterScheduleCard;
